/**
 * Shared funnel-stage breakdown types + reader helpers.
 *
 * The producer side (`computeStageBreakdown` in historical-metrics) writes
 * these shapes into `DiscoveryMetric.stageBreakdown` /
 * `DiscoveryMetric.tagBreakdown` / `AttributeMentionMetric.stageBreakdown`.
 * The consumer side (the historical dashboard's funnel hero + drill-down)
 * reads them via the helpers below to drive platform-scoped, stage-scoped
 * views. Pure types and pure functions — safe to import from client
 * components via `@repo/core/client`.
 */

import type { FunnelStage } from "../types/prompt-coverage";
export type { FunnelStage };

/**
 * Platform selector for the funnel hero / drill-down — five tracked AI
 * platforms plus the rolled-up "all" bucket. Mirrors the keys produced by
 * `normalizePlatformKey` in historical-metrics. Used as a UI dimension on
 * the dashboard.
 */
export type BreakdownPlatform =
	| "all"
	| "chatgpt"
	| "gemini"
	| "perplexity"
	| "claude"
	| "googleAiOverview";

/**
 * A single tracked platform — `BreakdownPlatform` without the rolled-up
 * `"all"` bucket. Every per-platform storage key in the codebase (stage
 * breakdown, tag breakdown, competitor slices) uses exactly these five
 * strings, so filters, writers and readers share one vocabulary.
 */
export type PlatformKey = Exclude<BreakdownPlatform, "all">;

/** The five tracked platform keys, in a fixed order (UI selects, tests). */
export const PLATFORM_KEYS: readonly PlatformKey[] = [
	"chatgpt",
	"gemini",
	"perplexity",
	"claude",
	"googleAiOverview",
];

/** Per-(stage × platform) cell of `DiscoveryMetric.stageBreakdown`. */
export interface DiscoveryStageCell {
	mentions: number;
	opportunities: number;
	/** Share of voice, 0–100, already rounded. */
	sov: number;
	citations: number;
}

/** Per-stage buckets keyed by platform (with "all" rolled up). */
export type DiscoveryStageBuckets = {
	all: DiscoveryStageCell;
} & Partial<Record<Exclude<BreakdownPlatform, "all">, DiscoveryStageCell>>;

/** Stored shape of `DiscoveryMetric.stageBreakdown`. */
export type DiscoveryStageBreakdown = Record<
	FunnelStage,
	DiscoveryStageBuckets
>;

/**
 * Stored shape of `DiscoveryMetric.tagBreakdown` — keyed by custom tag, each
 * value the same per-stage / per-platform map as `stageBreakdown`. Empty
 * `{}` for properties that have no tagged prompts yet (Part D adds tags).
 */
export type DiscoveryTagBreakdown = Record<string, DiscoveryStageBreakdown>;

/**
 * Reader-facing per-stage cell of `AttributeMentionMetric.stageBreakdown`.
 * The matcher persists `mentionCount` / `totalExecutions` (matching the
 * verbose names on the parent metric row); the reader translates to the
 * shorter `mentions` / `executions` aliases for the dashboard so it doesn't
 * have to know the persisted shape.
 */
export interface AttributeStageCell {
	mentions: number;
	executions: number;
	/** mention rate, 0–100 (matcher stores it with one decimal). */
	mentionRate: number;
}

/** Persisted shape of one stage cell — matches the matcher writer exactly. */
interface PersistedAttributeStageCell {
	mentionCount: number;
	totalExecutions: number;
	mentionRate: number;
}

/**
 * Per-stage buckets in the nested `AttributeMentionMetric.stageBreakdown`
 * shape (matcher writes this from v2 onward). Symmetric with
 * `DiscoveryStageBuckets`: an `all` cell rolled up across platforms, plus an
 * optional cell per tracked platform. The dashboard's Awareness segment uses
 * the per-platform cell to honor the platform filter.
 */
export type AttributeStageBuckets = {
	all: PersistedAttributeStageCell;
} & Partial<
	Record<Exclude<BreakdownPlatform, "all">, PersistedAttributeStageCell>
>;

/**
 * Stored shape of `AttributeMentionMetric.stageBreakdown`. Two shapes accepted
 * by the reader for backward compatibility:
 *
 * - **v1 (flat)** — `{ awareness?: PersistedAttributeStageCell, ... }`. Used
 *   by every row written before the matcher learned to split per platform.
 *   The reader treats these as platform-agnostic and returns the cell
 *   regardless of the platform filter.
 * - **v2 (nested)** — `{ awareness?: { all, chatgpt?, ... }, ... }`. Written
 *   by the current matcher. The reader honors the platform filter and falls
 *   back to `"all"` when the selected platform isn't represented.
 */
export type AttributeStageBreakdown = Partial<
	Record<FunnelStage, PersistedAttributeStageCell | AttributeStageBuckets>
>;

/** Cast `unknown` JSON from the wire to a typed `DiscoveryStageBreakdown` after
 * a light shape check. Returns `null` for missing / malformed values so
 * callers can fall back cleanly. */
export function coerceDiscoveryStageBreakdown(
	value: unknown,
): DiscoveryStageBreakdown | null {
	if (!value || typeof value !== "object") return null;
	const obj = value as Record<string, unknown>;
	if (!obj.awareness || !obj.consideration || !obj.decision) return null;
	return value as DiscoveryStageBreakdown;
}

/** Same lenient shape check for the tag breakdown — a record of named
 * `DiscoveryStageBreakdown` values. */
export function coerceDiscoveryTagBreakdown(
	value: unknown,
): DiscoveryTagBreakdown | null {
	if (!value || typeof value !== "object") return null;
	return value as DiscoveryTagBreakdown;
}

/** Same lenient shape check for the attribute stage breakdown. */
export function coerceAttributeStageBreakdown(
	value: unknown,
): AttributeStageBreakdown | null {
	if (!value || typeof value !== "object") return null;
	return value as AttributeStageBreakdown;
}

/**
 * Read a discovery stage cell from a stored breakdown, honoring the
 * selected platform. Falls back to the stage's `"all"` bucket when the
 * platform-specific cell is missing. Returns `null` if the stage itself
 * isn't present (legacy rows written before the breakdown was added).
 *
 * The `"all"` fallback is right for a *stage* read — a stage-scoped number
 * under a platform we don't break out is still better than nothing. It is
 * wrong for a *per-platform* surface, where inheriting the rolled-up value
 * makes an untracked platform look like it matches the brand-wide average.
 * Use `readDiscoveryCellExact` there.
 */
export function readDiscoveryCell(
	breakdown: DiscoveryStageBreakdown | null | undefined,
	stage: FunnelStage,
	platform: BreakdownPlatform,
): DiscoveryStageCell | null {
	const exact = readDiscoveryCellExact(breakdown, stage, platform);
	if (exact) return exact;
	if (!breakdown || platform === "all") return null;
	return breakdown[stage]?.all ?? null;
}

/**
 * Same read with no `"all"` fallback: returns `null` when the selected
 * platform has no cell at this stage. `computeStageBreakdown` only creates a
 * per-platform cell when an execution exists for it, so `null` here means
 * "this platform wasn't run at this stage" — which a per-platform chip must
 * render as "no data", not as the brand-wide number.
 */
export function readDiscoveryCellExact(
	breakdown: DiscoveryStageBreakdown | null | undefined,
	stage: FunnelStage,
	platform: BreakdownPlatform,
): DiscoveryStageCell | null {
	if (!breakdown) return null;
	const buckets = breakdown[stage];
	if (!buckets) return null;
	if (platform === "all") return buckets.all ?? null;
	return buckets[platform] ?? null;
}

/**
 * Read an attribute stage cell, honoring the selected platform when the
 * underlying row uses the v2 nested shape. Legacy v1 rows (flat per-stage
 * cells with no platform dimension) are returned as-is regardless of the
 * platform filter — there's no per-platform data to retrieve.
 *
 * Translates the persisted `mentionCount` / `totalExecutions` into the
 * shorter `mentions` / `executions` names the dashboard consumes.
 */
export function readAttributeCell(
	breakdown: AttributeStageBreakdown | null | undefined,
	stage: FunnelStage,
	platform: BreakdownPlatform,
): AttributeStageCell | null {
	if (!breakdown) return null;
	const raw = breakdown[stage];
	if (!raw) return null;

	// v1 flat shape — has `mentionCount` directly on the stage object. Return
	// it regardless of platform filter; there's no per-platform data here.
	if (typeof (raw as PersistedAttributeStageCell).mentionCount === "number") {
		const flat = raw as PersistedAttributeStageCell;
		return {
			mentions: flat.mentionCount ?? 0,
			executions: flat.totalExecutions ?? 0,
			mentionRate: flat.mentionRate ?? 0,
		};
	}

	// v2 nested shape — has an `all` bucket and optional per-platform buckets.
	const buckets = raw as AttributeStageBuckets;
	const cell =
		platform === "all" ? buckets.all : (buckets[platform] ?? buckets.all);
	if (!cell) return null;
	return {
		mentions: cell.mentionCount ?? 0,
		executions: cell.totalExecutions ?? 0,
		mentionRate: cell.mentionRate ?? 0,
	};
}

// ---------------------------------------------------------------------------
// Windowed aggregation — the single definition of a Share of Voice number
// ---------------------------------------------------------------------------

/**
 * Did a DiscoveryMetric row measure `platform`? `measuredPlatforms` holds the
 * raw execution platform ids the row counted (`"openai"`, `"claude"`, ...).
 * A platform missing from a non-empty list was not measured that day (e.g.
 * Claude batch results that have not landed yet): read it as "no data", never
 * as 0. Rows written before the column existed carry an empty list and are
 * treated as measuring everything, exactly as before. `"all"` is always
 * measured.
 */
export function isPlatformMeasured(
	measuredPlatforms: readonly string[] | null | undefined,
	platform: BreakdownPlatform,
): boolean {
	if (platform === "all") return true;
	if (!measuredPlatforms || measuredPlatforms.length === 0) return true;
	return measuredPlatforms.some(
		(raw) => normalizePlatformKey(raw) === platform,
	);
}

/** Minimal shape of a `DiscoveryMetric` row as the dashboards consume it. */
export interface DiscoveryMetricWindowRow {
	measuredAt: Date | string;
	/** See `isPlatformMeasured`. Absent/empty on legacy rows. */
	measuredPlatforms?: readonly string[] | null;
	/** Legacy all-stage column — used only when no row carries a breakdown. */
	shareOfVoice?: number | null;
	totalMentions?: number | null;
	stageBreakdown?: unknown;
	tagBreakdown?: unknown;
}

export interface StageWindowTotals {
	mentions: number;
	opportunities: number;
	/** 0–100 with one decimal; `null` when the window has no opportunities. */
	sov: number | null;
	/** True when the value came from the legacy all-stage column, not a breakdown. */
	fromLegacyColumn: boolean;
	/**
	 * How many measured days (post-collapse rows) fell inside the window.
	 *
	 * Zero means the brand was **never measured** in this window — the
	 * nightly runs paused, the property is new, or the range predates
	 * tracking. That is categorically different from a window that was
	 * measured and found no mentions (`measuredDays > 0`, `mentions === 0`),
	 * and every surface must say so rather than printing a 0 or a delta
	 * against a window that never happened.
	 */
	measuredDays: number;
}

export interface StageWindowOptions {
	/** Stages to pool. One entry for a band segment, two for the headline. */
	stages: readonly FunnelStage[];
	platform?: BreakdownPlatform;
	tag?: string | null;
	/** When false (the default), a platform with no cell at a stage
	 *  contributes nothing rather than inheriting the stage's rolled-up
	 *  `"all"` bucket: a per-platform number must never be the brand-wide
	 *  one under that platform's name. */
	fallbackToAll?: boolean;
}

const EMPTY_TOTALS: StageWindowTotals = {
	mentions: 0,
	opportunities: 0,
	sov: null,
	fromLegacyColumn: false,
	measuredDays: 0,
};

const dayKey = (d: Date | string) => new Date(d).toISOString().slice(0, 10);

/**
 * Collapse to one row per UTC day, latest `measuredAt` winning. A day can
 * carry both a report row and a nightly row; pooling both would double-count
 * every answer. Exported for the competitor window pooling in
 * `headline-sov.ts`, which must collapse on exactly the same rule.
 */
export function collapseRowsByDay<T extends DiscoveryMetricWindowRow>(
	rows: readonly T[],
): T[] {
	const byDay = new Map<string, T>();
	for (const row of rows) {
		const key = dayKey(row.measuredAt);
		const prev = byDay.get(key);
		if (
			!prev ||
			new Date(row.measuredAt).getTime() >
				new Date(prev.measuredAt).getTime()
		) {
			byDay.set(key, row);
		}
	}
	return [...byDay.values()].sort(
		(a, b) =>
			new Date(a.measuredAt).getTime() - new Date(b.measuredAt).getTime(),
	);
}

/**
 * Pool Share of Voice across a time window as a **ratio of sums** — total
 * brand mentions ÷ total answer opportunities — rather than a mean of daily
 * rates.
 *
 * Two reasons this is the shape every SoV surface should use:
 *
 * 1. **The stages compose exactly.** Pooling consideration and decision
 *    separately and pooling them together give the same numerator and
 *    denominator, so a headline scoped to `["consideration", "decision"]`
 *    reconciles against the per-stage numbers a user sees beside it. A mean
 *    of daily rates does not — the headline and the band would disagree with
 *    no visible explanation.
 * 2. **Low-volume days stop dominating.** A mean of daily rates weights a day
 *    with 8 tracked prompts exactly as heavily as a day with 30, so adding
 *    prompts to a property visibly moves the trend for reasons that have
 *    nothing to do with the brand's visibility.
 *
 * Reads `mentions` / `opportunities` off each cell rather than the stored
 * per-cell `sov`, which is pre-rounded to an integer at write time.
 *
 * Rows with no `stageBreakdown` fall back to the legacy all-stage
 * `shareOfVoice` column, but only when *no* row in the window carries a
 * breakdown — mixing a pooled ratio with pre-computed percentages would be
 * meaningless. Note the legacy column spans all three stages, so a legacy
 * property's number is not stage-scoped; `fromLegacyColumn` flags that.
 *
 * Scoped to one platform, a day that did not measure it (`isPlatformMeasured`)
 * is skipped entirely: it adds no answers and is not a measured day, so a
 * Claude window is Claude's measured days only, never diluted by days whose
 * Claude results had not landed.
 */
export function aggregateStageWindow(
	rows: readonly DiscoveryMetricWindowRow[],
	{
		stages,
		platform = "all",
		tag = null,
		fallbackToAll = false,
	}: StageWindowOptions,
): StageWindowTotals {
	if (!rows.length) return EMPTY_TOTALS;

	const daily = collapseRowsByDay(rows).filter((row) =>
		isPlatformMeasured(row.measuredPlatforms, platform),
	);
	if (!daily.length) return EMPTY_TOTALS;
	const read = fallbackToAll ? readDiscoveryCell : readDiscoveryCellExact;

	let mentions = 0;
	let opportunities = 0;
	let sawBreakdown = false;

	for (const row of daily) {
		const breakdown = tag
			? (coerceDiscoveryTagBreakdown(row.tagBreakdown)?.[tag] ?? null)
			: coerceDiscoveryStageBreakdown(row.stageBreakdown);
		if (!breakdown) continue;
		sawBreakdown = true;
		for (const stage of stages) {
			const cell = read(breakdown, stage, platform);
			if (!cell) continue;
			mentions += cell.mentions;
			opportunities += cell.opportunities;
		}
	}

	if (sawBreakdown) {
		return {
			mentions,
			opportunities,
			sov:
				opportunities > 0
					? Math.round((mentions / opportunities) * 1000) / 10
					: null,
			fromLegacyColumn: false,
			measuredDays: daily.length,
		};
	}

	// A tag-scoped view with no tagged data is genuinely empty — never let it
	// fall through to brand-wide numbers masquerading as the tag's. The days
	// were still measured, though: the brand ran, this tag just wasn't on
	// any of it.
	if (tag) return { ...EMPTY_TOTALS, measuredDays: daily.length };

	const legacy = daily.filter((r) => typeof r.shareOfVoice === "number");
	if (!legacy.length) return { ...EMPTY_TOTALS, measuredDays: daily.length };
	const meanSov =
		legacy.reduce((sum, r) => sum + (r.shareOfVoice ?? 0), 0) /
		legacy.length;
	return {
		mentions: legacy.reduce((sum, r) => sum + (r.totalMentions ?? 0), 0),
		opportunities: 0,
		sov: Math.round(meanSov * 10) / 10,
		fromLegacyColumn: true,
		measuredDays: legacy.length,
	};
}

export interface StageWindowDelta {
	current: StageWindowTotals;
	previous: StageWindowTotals;
	/** Change in percentage points, one decimal. Null when either side is empty. */
	delta: number | null;
}

/**
 * `aggregateStageWindow` over the selected window and the one immediately
 * before it, for the "vs previous N days" badge. Window bounds match
 * `aggregateWindowDelta`: `(end - days, end]` and `(end - 2·days, end - days]`.
 */
export function aggregateStageWindowDelta(
	rows: readonly DiscoveryMetricWindowRow[],
	options: StageWindowOptions & { end: Date; days: number },
): StageWindowDelta {
	const { end, days, ...windowOptions } = options;
	const endMs = end.getTime();
	const windowMs = days * 24 * 60 * 60 * 1000;
	const currentStart = endMs - windowMs;
	const previousStart = endMs - 2 * windowMs;

	const slice = (from: number, to: number) =>
		rows.filter((row) => {
			const ts = new Date(row.measuredAt).getTime();
			return ts > from && ts <= to;
		});

	const current = aggregateStageWindow(
		slice(currentStart, endMs),
		windowOptions,
	);
	const previous = aggregateStageWindow(
		slice(previousStart, currentStart),
		windowOptions,
	);
	const delta =
		current.sov !== null && previous.sov !== null
			? Math.round((current.sov - previous.sov) * 10) / 10
			: null;

	return { current, previous, delta };
}

/**
 * Normalize a raw platform identifier to one of the five tracked platform
 * keys. Mirror of the same function in `historical-metrics.ts`; lives here
 * so client-side breakdown writers (e.g. the attribute matcher) and the
 * dashboard share a single mapping. Returns `null` for platforms we don't
 * break out — those executions still count toward the rolled-up "all" cell.
 */
export function normalizePlatformKey(
	platform: string | null | undefined,
): Exclude<BreakdownPlatform, "all"> | null {
	const p = (platform || "").toLowerCase();
	if (p.includes("perplexity")) return "perplexity";
	if (p.includes("claude") || p.includes("anthropic")) return "claude";
	if (p === "google_ai_overview" || p.includes("overview"))
		return "googleAiOverview";
	if (p.includes("gemini")) return "gemini";
	if (p === "openai" || p.includes("chatgpt")) return "chatgpt";
	return null;
}
